import { useEffect, useState } from "react";
import { ArrowUpRight, Instagram as IgIcon, Play } from "lucide-react";
import { MaskTitle, Reveal, SectionLabel } from "./motion";
import { api } from "@/lib/api";
import { DRIVER, INSTAGRAM_URL } from "@/lib/content";
import { useLang } from "@/context/LangContext";

const imgSrc = (u) => (u?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

const PostTile = ({ p, i, label }) => (
  <a href={p.permalink} target="_blank" rel="noopener noreferrer" data-testid={`insta-post-${i}`}
    className="group relative block aspect-[4/5] overflow-hidden bg-surface">
    {p.image_url && <img src={imgSrc(p.image_url)} alt={p.caption?.slice(0, 80) || "Instagram"} loading="lazy" className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.1s] ease-out group-hover:scale-105" />}
    {p.media_type === "VIDEO" && <Play className="absolute right-4 top-4 h-5 w-5 fill-white text-white" />}
    <div className="absolute inset-0 hidden flex-col justify-end bg-gradient-to-t from-ink/90 via-ink/10 to-transparent p-5 opacity-0 transition-opacity duration-500 group-hover:opacity-100 md:flex">
      {p.caption && <p className="line-clamp-2 text-sm text-white">{p.caption}</p>}
      <span className="label-mono mt-3 text-[11px] text-white">{label} →</span>
    </div>
  </a>
);

const Placeholder = ({ i, label }) => (
  <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid={`insta-placeholder-${i}`}
    className="group grid aspect-[4/5] place-items-center border border-dashed border-white/15 bg-surface transition-colors duration-500 hover:border-volt">
    <span className="flex flex-col items-center gap-3 text-white/70 group-hover:text-white">
      <IgIcon className="h-6 w-6 md:h-8 md:w-8" />
      <span className="label-mono hidden text-[11px] md:block">{label}</span>
    </span>
  </a>
);

export const InstagramFeed = () => {
  const { t, lang } = useLang();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    let alive = true;
    api.get("/instagram/posts")
      .then((r) => { if (alive) setPosts(Array.isArray(r.data?.posts) ? r.data.posts.slice(0, 3) : []); })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  return (
    <section id="instagram" data-testid="instagram-section" className="bg-ink px-5 pb-24 md:px-10 md:pb-36">
      <div className="mx-auto max-w-[1600px] border-t border-white/10 pt-24 md:pt-36">
        <SectionLabel index="02">{t.insta.label}</SectionLabel>
        <MaskTitle key={lang} lines={[t.insta.title[0], { text: t.insta.title[1], className: "text-volt" }]}
          className="mt-6 font-display text-6xl font-black uppercase leading-[0.85] md:text-8xl lg:text-9xl" />

        <div className="mt-14 grid grid-cols-3 gap-2 md:gap-4" data-testid="insta-grid">
          {posts.length > 0
            ? posts.map((p, i) => <Reveal key={p.permalink || i} delay={i * 0.08}><PostTile p={p} i={i} label={t.insta.open} /></Reveal>)
            : [0, 1, 2].map((i) => <Reveal key={i} delay={i * 0.08}><Placeholder i={i} label={t.insta.soon} /></Reveal>)}
        </div>

        <Reveal className="mt-4">
          <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer" data-testid="insta-profile-btn"
            className="group relative flex flex-col items-start justify-between gap-6 overflow-hidden bg-trident p-6 text-white md:flex-row md:items-center md:p-12">
            <span className="absolute inset-0 origin-left scale-x-0 bg-white transition-transform duration-700 ease-[cubic-bezier(0.76,0,0.24,1)] group-hover:scale-x-100" />
            <span className="relative flex items-center gap-5 transition-colors duration-700 group-hover:text-ink">
              <span className="grid h-12 w-12 shrink-0 place-items-center rounded-xl border-2 border-current md:h-20 md:w-20 md:rounded-2xl">
                <IgIcon className="h-6 w-6 md:h-10 md:w-10" />
              </span>
              <span className="min-w-0">
                <span className="label-mono block text-[11px]">{t.insta.follow}</span>
                <span className="mt-2 block break-all font-display text-[7vw] font-black uppercase leading-none sm:text-4xl md:text-6xl">@{DRIVER.instagram}</span>
              </span>
            </span>
            <span className="relative grid h-14 w-14 place-items-center rounded-full border border-current transition-[color,transform] duration-700 group-hover:rotate-45 group-hover:text-ink">
              <ArrowUpRight className="h-6 w-6" />
            </span>
          </a>
        </Reveal>
      </div>
    </section>
  );
};
